import { useEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'nmi-theme';

export function resolveTheme(preference, prefersDark) {
  if (preference === 'dark' || preference === 'light') return preference;
  return prefersDark ? 'dark' : 'light';
}

function getStoredPreference() {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

function getSystemPreference() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

export function applyTheme(theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export function getInitialTheme() {
  return resolveTheme(getStoredPreference(), getSystemPreference());
}

export function initializeTheme() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 'light';
  const theme = getInitialTheme();
  applyTheme(theme);
  return theme;
}

export function setStoredTheme(theme) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // The in-session theme still works if storage is unavailable.
  }
  applyTheme(theme);
  return theme;
}

export function useTheme() {
  const [theme, setTheme] = useState(() => getInitialTheme());

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mediaQuery || getStoredPreference()) return undefined;

    const followSystemTheme = (event) => {
      const nextTheme = resolveTheme(null, event.matches);
      setTheme(nextTheme);
      applyTheme(nextTheme);
    };

    mediaQuery.addEventListener('change', followSystemTheme);
    return () => mediaQuery.removeEventListener('change', followSystemTheme);
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    setStoredTheme(nextTheme);
  };

  return { theme, toggleTheme };
}
